import {
  DeleteRequest,
  PutRequest,
  useLazySamplesAirMicrobialSampleSetListQuery,
  useSamplesAirMicrobialSampleSetDeleteMutation,
  useSamplesAirMicrobialSampleSetListQuery,
  useSamplesAirMicrobialSampleSetPutMutation,
  useSamplesAirMicrobialSampleSetShowQuery,
} from "@/services/backend/samples/api/air-microbial-api";
import { AirMicrobialSampleSet } from "@/services/backend/samples/types/air-microbial-sample";
import { UUID } from "@bitory-gmbh/nidavellir";
import {
  documentDbGetAll,
  documentDbGetAllDeleted,
  documentDbPut,
} from "@/shared/lib/storage/indexdb/document-db";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import t from "@/lang/lang";
import {
  Mutation,
  Query,
  useDocumentDBDelete,
  useDocumentDBGet,
  useDocumentDBGetAll,
  useDocumentDBPut,
} from "@/shared/lib/storage/indexdb/document-db-hooks";
import { useParams } from "react-router";
import { FilterMode } from "@/shared/lib/filter/filter";
import { useStaleContext } from "./stale-context";
import { OfflineState, useOfflineContext } from "./offline-context";

/*
 * useAirMicrobialSampleSetOffline prepares two functions. One to
 * prepare the air microbial sample sets for offline usage and one to
 * sync the offline state back to the server.
 *
 * When calling the functions, the offline database must
 * be set up. The caller must catch thrown errors.
 */
export function useAirMicrobialSampleSetOffline(workOrderId: UUID) {
  const { setSynced } = useOfflineContext();
  const { addStale } = useStaleContext();
  const [load] = useLazySamplesAirMicrobialSampleSetListQuery();
  const [put] = useSamplesAirMicrobialSampleSetPutMutation();
  const [doDelete] = useSamplesAirMicrobialSampleSetDeleteMutation();

  const prepMsg = t("Speichere Luftkeimproben ...");
  const syncMsg = t("Synchronisiere Luftkeimproben ...");
  const doneMsg = t("Luftkeimproben");

  const prepareAirMicrobialSampleSets = async () => {
    // Load the latest state
    const result = await load({
      document: { active: true, values: [workOrderId] },
    });
    if (result.error) {
      throw result.error;
    }

    // When going offline, data is set to stale, such that
    // all data is reloaded from the offline database.
    addStale("airMicrobialSampleSet");

    // Append to synced state for user information
    setSynced((prev) => [...prev, prepMsg]);

    // Save to index DB
    await Promise.all(
      result.data!.sampleSets.map(async (sampleSet) => {
        await documentDbPut<AirMicrobialSampleSet>(
          GSMDocumentKind.WorkOrder,
          workOrderId,
          "airMicrobialSampleSet",
          sampleSet,
        );
      }),
    );

    // Replace in synced state for user information
    setSynced((prev) => prev.map((p) => (p === prepMsg ? doneMsg : p)));
  };

  const syncAirMicrobialSampleSets = async () => {
    // Append to synced state for user information
    setSynced((prev) => [...prev, syncMsg]);

    // Load from index DB
    const idbSampleSets = await documentDbGetAll<AirMicrobialSampleSet>(
      GSMDocumentKind.WorkOrder,
      workOrderId,
      "airMicrobialSampleSet",
    );
    const deletedIdbSampleSets =
      await documentDbGetAllDeleted<AirMicrobialSampleSet>(
        GSMDocumentKind.WorkOrder,
        workOrderId,
        "airMicrobialSampleSet",
      );

    // Sync to server
    // We first PUT all non-deleted or new entities back to the server
    await Promise.all(
      idbSampleSets.map(async (sampleSet) => {
        await put(sampleSet).unwrap();
      }),
    );
    // Then we delete the entities that where deleted offline.
    await Promise.all(
      deletedIdbSampleSets.map(async (sampleSet) => {
        await doDelete({ id: sampleSet.id }).unwrap();
      }),
    );

    // Replace in synced state for user information
    setSynced((prev) => prev.map((p) => (p === syncMsg ? doneMsg : p)));
  };

  return { prepareAirMicrobialSampleSets, syncAirMicrobialSampleSets };
}

export function usePutAirMicrobialSampleSet(): Mutation<
  AirMicrobialSampleSet,
  AirMicrobialSampleSet
> {
  const { workOrderId } = useParams();
  const { offlineState } = useOfflineContext();

  const [
    putAirMicrobialSampleSetMutation,
    { isLoading, error, isSuccess, reset },
  ] = useSamplesAirMicrobialSampleSetPutMutation();

  const [
    putAirMicrobialSampleSetIndexedDB,
    {
      isLoading: idbIsLoading,
      error: idbError,
      isSuccess: idbIsSuccess,
      reset: idbReset,
    },
  ] = useDocumentDBPut<PutRequest, AirMicrobialSampleSet>(
    GSMDocumentKind.WorkOrder,
    workOrderId!,
    "airMicrobialSampleSet",
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return [
        putAirMicrobialSampleSetMutation,
        { isLoading, error, isSuccess, reset },
      ];
    case OfflineState.OFFLINE:
      return [
        putAirMicrobialSampleSetIndexedDB,
        {
          isLoading: idbIsLoading,
          error: idbError,
          isSuccess: idbIsSuccess,
          reset: idbReset,
        },
      ];
    default:
      return [
        () => {
          throw new Error(
            `Put of sample set not possible in state ${offlineState}.`,
          );
        },
        {
          isLoading: false,
          error: undefined,
          isSuccess: false,
          reset: () => null,
        },
      ];
  }
}

export function useGetAirMicrobialSampleSet(
  id: string,
): Query<AirMicrobialSampleSet> {
  const { workOrderId } = useParams();
  const { offlineState } = useOfflineContext();

  const {
    data: serverData,
    isLoading,
    error,
  } = useSamplesAirMicrobialSampleSetShowQuery({
    id,
  });

  const {
    data: idbData,
    isLoading: idbIsLoading,
    error: idbError,
  } = useDocumentDBGet<AirMicrobialSampleSet>(
    GSMDocumentKind.WorkOrder,
    workOrderId!,
    "airMicrobialSampleSet",
    id,
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return {
        data: serverData,
        isLoading,
        error,
      };
    case OfflineState.OFFLINE:
      return {
        data: idbData,
        isLoading: idbIsLoading,
        error: idbError,
      };
    default:
      return {
        data: undefined,
        isLoading: false,
        error: undefined,
      };
  }
}

export function useListAirMicrobialSampleSets() {
  const { workOrderId } = useParams();
  const { offlineState } = useOfflineContext();

  const {
    data: serverData,
    isLoading,
    error,
  } = useSamplesAirMicrobialSampleSetListQuery({
    document: {
      active: true,
      mode: FilterMode.Or,
      values: [workOrderId!],
    },
  });

  const {
    data: idbData,
    isLoading: idbIsLoading,
    error: idbError,
  } = useDocumentDBGetAll<AirMicrobialSampleSet>(
    GSMDocumentKind.WorkOrder,
    workOrderId!,
    "airMicrobialSampleSet",
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return {
        data: serverData?.sampleSets,
        isLoading,
        error,
      };
    case OfflineState.OFFLINE:
      return {
        data: idbData,
        isLoading: idbIsLoading,
        error: idbError,
      };
    default:
      return {
        data: undefined,
        isLoading: false,
        error: undefined,
      };
  }
}

export function useDeleteAirMicrobialSampleSet(): Mutation<
  DeleteRequest,
  null
> {
  const { workOrderId } = useParams();
  const { offlineState } = useOfflineContext();

  const [
    deleteAirMicrobialSampleSetMutation,
    { isLoading, error, isSuccess, reset },
  ] = useSamplesAirMicrobialSampleSetDeleteMutation();

  const [
    deleteAirMicrobialSampleSetIndexedDB,
    {
      isLoading: idbIsLoading,
      error: idbError,
      isSuccess: idbIsSuccess,
      reset: idbReset,
    },
  ] = useDocumentDBDelete(
    GSMDocumentKind.WorkOrder,
    workOrderId!,
    "airMicrobialSampleSet",
  );
  const deleteIdb = (request: DeleteRequest) =>
    deleteAirMicrobialSampleSetIndexedDB(request.id);

  switch (offlineState) {
    case OfflineState.ONLINE:
      return [
        deleteAirMicrobialSampleSetMutation,
        { isLoading, error, isSuccess, reset },
      ];
    case OfflineState.OFFLINE:
      return [
        deleteIdb,
        {
          isLoading: idbIsLoading,
          error: idbError,
          isSuccess: idbIsSuccess,
          reset: idbReset,
        },
      ];
    default:
      return [
        () => {
          throw new Error(
            `Delete of sample set not possible in state ${offlineState}.`,
          );
        },
        {
          isLoading: false,
          error: undefined,
          isSuccess: false,
          reset: () => null,
        },
      ];
  }
}
